<script setup>
import { computed, ref, nextTick } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRouter } from 'vue-router';

// Uma fileira de abas navegavel por teclado. Abaixo de 1024 px a fileira vira
// um seletor: cinco areas mais nove sub-abas nao cabem num celular sem virar
// rolagem horizontal da pagina.
//
// Ativacao manual (padrao WAI-ARIA de abas que carregam tela): as setas, Home
// e End so movem o foco; Enter ou Espaco abrem a aba. Antes cada seta ja
// navegava e recarregava o relatorio no meio do caminho.

const props = defineProps({
  items: {
    type: Array,
    required: true,
  },
  activeId: {
    type: String,
    default: '',
  },
  label: {
    type: String,
    required: true,
  },
  // Aba de area tem icone; sub-aba nao.
  showIcon: {
    type: Boolean,
    default: false,
  },
  query: {
    type: Object,
    default: () => ({}),
  },
  // Prefixo do id de cada aba: as duas fileiras (areas e sub-abas) nao podem
  // gerar o mesmo id, e o painel aponta para a aba ativa pelo id.
  idPrefix: {
    type: String,
    default: 'reports-tab',
  },
});

const { t } = useI18n();
const router = useRouter();
const tablistRef = ref(null);

const targetFor = item => ({ name: item.routeName, query: { ...props.query } });

// A aba ativa pode nao estar na lista (CSAT aberto por link direto numa conta
// sem uso): nesse caso a primeira fica no fluxo do Tab, senao nenhuma ficava.
const activeIndex = computed(() =>
  Math.max(
    props.items.findIndex(item => item.id === props.activeId),
    0
  )
);

const tabNodes = () =>
  Array.from(tablistRef.value?.querySelectorAll('[role="tab"]') || []);

const focusTab = async index => {
  const total = props.items.length;
  if (!total) return;
  const next = ((index % total) + total) % total;
  await nextTick();
  tabNodes()[next]?.focus();
};

const currentFocusIndex = () => {
  const index = tabNodes().indexOf(document.activeElement);
  return index === -1 ? activeIndex.value : index;
};

const activate = index => {
  const item = props.items[index];
  if (item) router.push(targetFor(item));
};

const onKeydown = event => {
  const current = currentFocusIndex();
  const moves = {
    ArrowRight: current + 1,
    ArrowLeft: current - 1,
    Home: 0,
    End: props.items.length - 1,
  };
  if (event.key in moves) {
    event.preventDefault();
    focusTab(moves[event.key]);
    return;
  }
  // Enter ja abre o link nativamente; Espaco num link rolaria a pagina.
  if (event.key === ' ') {
    event.preventDefault();
    activate(current);
  }
};

const onSelect = event => {
  const item = props.items.find(entry => entry.id === event.target.value);
  if (item) router.push(targetFor(item));
};
</script>

<template>
  <div>
    <div
      ref="tablistRef"
      role="tablist"
      :aria-label="label"
      class="hidden lg:flex flex-wrap items-center gap-1.5"
      @keydown="onKeydown"
    >
      <router-link
        v-for="(item, index) in items"
        :id="`${idPrefix}-${item.id}`"
        :key="item.id"
        role="tab"
        :aria-selected="item.id === activeId ? 'true' : 'false'"
        aria-controls="reports-tabpanel"
        :tabindex="index === activeIndex ? 0 : -1"
        :to="targetFor(item)"
        class="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border text-sm font-medium transition-colors outline-none focus-visible:ring-2 focus-visible:ring-n-brand"
        :class="
          item.id === activeId
            ? 'bg-n-slate-12 border-n-slate-12 text-n-solid-1'
            : 'border-n-strong text-n-slate-11 hover:bg-n-alpha-2'
        "
      >
        <span
          v-if="showIcon && item.icon"
          :class="item.icon"
          class="size-4 shrink-0"
        />
        {{ t(item.labelKey) }}
      </router-link>
    </div>

    <label class="flex lg:hidden flex-col gap-1.5">
      <span class="text-xs font-medium text-n-slate-11">{{ label }}</span>
      <select
        class="w-full px-3 py-2 text-sm rounded-xl border border-n-strong bg-n-solid-2 text-n-slate-12"
        :value="activeId"
        @change="onSelect"
      >
        <option v-for="item in items" :key="item.id" :value="item.id">
          {{ t(item.labelKey) }}
        </option>
      </select>
    </label>
  </div>
</template>
